<template>
  <el-row class="w-full">
    <el-col :span="24">
      <el-container class="app-nav-container">
        <el-aside width="auto">
          <RouterLink :to="homeRoute" class="app-nav-logo">
            <img :src="logo" alt="CR Credit Logo" class="app-nav-logo-img" />
          </RouterLink>
        </el-aside>
        <!-- Responsive Breadcrumb Navigation -->
        <el-main class="app-nav-menu-container">
          <el-menu v-if="isAuthenticated" mode="horizontal" :default-active="$route.path" class="app-nav-menu" router
            background-color="#fff" text-color="#606266" active-text-color="#409EFF">
            <el-menu-item :index="homePath">
              <el-icon>
                <House />
              </el-icon>
              <span class="menu-text" v-if="false">Dashboard</span>
            </el-menu-item>
            <el-menu-item :index="contractsPath">
              <el-icon>
                <Document />
              </el-icon>
              <span class="menu-text">Smlouvy</span>
            </el-menu-item>
            <el-menu-item :index="casesPath">
              <el-icon>
                <Money />
              </el-icon>
              <span class="menu-text">Pojistné události</span>
            </el-menu-item>
            <el-menu-item :index="msgPath">
              <el-icon>
                <Message />
              </el-icon>
              <span class="menu-text">Zprávy</span>
            </el-menu-item>
            <el-menu-item :index="myDataPath">
              <el-icon>
                <User />
              </el-icon>
              <span class="menu-text">Moje údaje</span>
            </el-menu-item>
          </el-menu>
        </el-main>
        <el-aside v-if="isAuthenticated" width="auto" class="search-aside">
          <el-button :icon="Search" size="small" class="header-search-input"
            @click="openSearchDialog">Hledat</el-button>
        </el-aside>
        <el-aside width="auto">
          <UserInfo v-if="isAuthenticated" />
        </el-aside>
      </el-container>
    </el-col>
    <el-col :span="24">
      <el-container class="app-nav-container">
        <!-- Responsive Breadcrumb Navigation -->
        <el-main v-if="isAuthenticated && shouldShowBreadcrumbs" class="breadcrumb-nav">
          <el-breadcrumb separator="/" class="breadcrumb">
            <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index"
              :class="{ 'is-last': index === breadcrumbs.length - 1 }">
              <RouterLinkSmart v-if="isBreadcrumbLink(item, index)" :to="item.path" class="breadcrumb-link">
                <el-icon v-if="item.icon" class="breadcrumb-icon">
                  <component :is="getIconComponent(item.icon)" />
                </el-icon>
                <span class="breadcrumb-text" :class="{ 'short-text': isSmallScreen }">
                  {{ isSmallScreen ? (item.shortLabel || item.label) : item.label }}
                </span>
              </RouterLinkSmart>
              <span v-else class="breadcrumb-static">
                <el-icon v-if="item.icon" class="breadcrumb-icon">
                  <component :is="getIconComponent(item.icon)" />
                </el-icon>
                <span class="breadcrumb-text" :class="{ 'short-text': isSmallScreen }">
                  {{ isSmallScreen ? (item.shortLabel || item.label) : item.label }}
                </span>
              </span>
            </el-breadcrumb-item>
          </el-breadcrumb>
        </el-main>
      </el-container>
    </el-col>
  </el-row>

  <!-- Search Results Dialog -->
  <SearchResultsDialog v-model="isSearchDialogVisible" />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import logo from '../../assets/logo_1.png'
import { ElBreadcrumb, ElBreadcrumbItem, ElIcon } from 'element-plus'
import { House, Document, Warning, Money, Search, Message, User } from '@element-plus/icons-vue'
import { useAuth } from '@/composables/useAuth'
import { useBreadcrumbs } from '@/composables/useBreadcrumbs'
import { useCustomerStore } from '@/stores/customer'
import { storeToRefs } from 'pinia'
import UserInfo from './UserInfo.vue'
import SearchResultsDialog from '@/components/search/SearchResultsDialog.vue'
import RouterLinkSmart from '@/components/common/RouterLinkSmart.vue'
import type { BreadcrumbItem } from '@/composables/useBreadcrumbs'

const { isAuthenticated } = useAuth()
const route = useRoute()
const router = useRouter()
const customerStore = useCustomerStore()
const { breadcrumbs } = useBreadcrumbs()
const { selectedClientId } = storeToRefs(customerStore)

// Search dialog state
const isSearchDialogVisible = ref(false)

const openSearchDialog = () => {
  isSearchDialogVisible.value = true
}

// Type-safe route objects for RouterLink
const homeRoute = computed(() => ({
  name: 'home' as const,
  params: { clientId: selectedClientId.value || Number(route.params.clientId || 0) },
}))

// Type-safe route paths for el-menu-item (requires path strings)
const homePath = computed(() => {
  const resolved = router.resolve(homeRoute.value)
  return resolved.path
})

const contractsPath = computed(() => {
  const clientId = selectedClientId.value || Number(route.params.clientId || 0)
  return router.resolve({
    name: 'contracts' as const,
    params: { clientId: clientId },
  }).path
})

const casesPath = computed(() => {
  const clientId = selectedClientId.value || Number(route.params.clientId || 0)
  return router.resolve({
    name: 'insurance-cases' as const,
    params: { clientId: clientId },
  }).path
})

const msgPath = computed(() => {
  const clientId = selectedClientId.value || Number(route.params.clientId || 0)
  return router.resolve({
    name: 'messages' as const,
    params: { clientId: clientId },
  }).path
})

const myDataPath = computed(() => {
  const clientId = selectedClientId.value || Number(route.params.clientId || 0)
  return router.resolve({
    name: 'my-data' as const,
    params: { clientId: clientId },
  }).path
})

// Only show breadcrumbs for main application routes, not auth/error pages
const shouldShowBreadcrumbs = computed(() => {
  const hiddenRoutes = ['callback', 'error', 'logout', 'not-found']
  return !hiddenRoutes.includes(route.name as string)
})

// Check if we're on a small screen
const isSmallScreen = computed(() => {
  return window.innerWidth <= 768
})

/** Real anchor href links (not el-breadcrumb-item :to), so new tab / middle-click work. */
function isBreadcrumbLink(item: BreadcrumbItem, index: number): boolean {
  if (index >= breadcrumbs.value.length - 1) return false
  if (item.loading) return false
  const p = item.path
  if (!p || p === '#') return false
  return true
}

// Icon component mapping
const getIconComponent = (iconName: string) => {
  const iconMap: Record<string, typeof House> = {
    House,
    Document,
    Warning,
    Money
  }
  return iconMap[iconName] || House
}
</script>

<style scoped>
.nav-aside {
  box-sizing: border-box;
  flex-shrink: 1;
  overflow: auto;
}


.app-nav-container {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
}

.app-nav-menu-container {
  align-items: center;
  justify-content: left;
  padding: 0px;
}

.app-nav-menu {
  flex: 1;
  margin-left: 40px;
  border-bottom: 0;
  --el-menu-base-level-padding: 15px;
}

.app-nav-logo {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.app-nav-logo-img {
  height: 32px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  transition: height 0.3s ease;
}

.breadcrumb-nav {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0px;
  padding-bottom: 15px;
}

.breadcrumb {
  font-size: 14px;
  width: 100%;
}
.breadcrumb-icon {
  margin-right: 4px;
  font-size: 14px;
}

.breadcrumb-link {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}

.breadcrumb-static {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}

.breadcrumb-link .breadcrumb-text {
  text-decoration: underline;
}

.breadcrumb-link:hover .breadcrumb-text {
  color: var(--el-color-primary-light-3);
}

.breadcrumb-text.short-text {
  font-size: 12px;
}

/* .breadcrumb :deep(.el-breadcrumb__item:last-child .el-breadcrumb__inner) {
  color: var(--el-text-color-primary);
  font-weight: 500;
}

.breadcrumb :deep(.el-breadcrumb__item:not(:last-child) .el-breadcrumb__inner) {
  color: var(--el-text-color-regular);
  transition: color 0.2s ease;
}

.breadcrumb :deep(.el-breadcrumb__item:not(:last-child) .el-breadcrumb__inner:hover) {
  color: var(--el-color-primary);
}
 */
/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .app-nav-container {
    height: 56px;
    padding: 0 12px;
  }

  .app-nav-logo-img {
    height: 28px;
  }

  .breadcrumb-nav {
    margin-left: 20px;
  }

  .breadcrumb {
    font-size: 13px;
  }

  .breadcrumb-icon {
    font-size: 12px;
    margin-right: 2px;
  }

  .breadcrumb-text.short-text {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .app-nav-container {
    height: 48px;
    padding: 0 8px;
  }

  .app-nav-logo-img {
    height: 24px;
  }

  .breadcrumb-nav {
    margin-left: 12px;
  }

  .breadcrumb {
    font-size: 12px;
  }

  .breadcrumb-icon {
    font-size: 11px;
    margin-right: 1px;
  }

  .breadcrumb-text.short-text {
    font-size: 10px;
  }
}

@media (max-width: 360px) {
  .app-nav-container {
    height: 44px;
  }

  .app-nav-logo-img {
    height: 20px;
  }

  .breadcrumb {
    font-size: 11px;
  }

  .breadcrumb-icon {
    font-size: 10px;
  }

  .breadcrumb-text.short-text {
    font-size: 9px;
  }
}

.search-aside {
  margin-right: 16px;
  display: flex;
  align-items: center;
}

.header-search-input {
  max-width: 150px;
    min-width: 50px;
}

/* Mobile responsive adjustments for search */
@media (max-width: 768px) {
  .search-aside {
    margin-right: 8px;
  }

  .header-search-input {
    width: 150px;
    min-width: 120px;
  }
}

@media (max-width: 480px) {
  .search-aside {
    margin-right: 4px;
  }

  .header-search-input {
    width: 120px;
    min-width: 100px;
  }
}
</style>


